import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { X } from 'lucide';
import base from '../styles/base.css?inline';
import { lucideIcon } from '../icon.js';
import { NEN5740_BRON, NEN5740_INTRO, NEN5740_UITLEG } from '../utils/nen5740-uitleg.js';

/**
 * nen5740-uitleg-dialog — 'Bekijk uitleg' bij stap 2 (Strategie) van het vooronderzoek.
 *
 * Links de tabbladen: eerst de introductie (hoe kies je een strategie), dan elke strategie die
 * GeoVoice kan uitrekenen. Rechts per strategie wanneer hij van toepassing is, en de tabel uit
 * de norm met de aantallen. De inhoud staat in utils/nen5740-uitleg.js.
 *
 * Events:
 *   close   de popup sluiten
 */
export class Nen5740UitlegDialog extends LitElement {
  static properties = {
    /** Het tabblad waarop de popup opent: 'intro' of een strategiecode. */
    start: { type: String },
    _tab: { state: true },
  };

  constructor() {
    super();
    this.start = 'intro';
    this._tab = null;
    this._onKey = (e) => { if (e.key === 'Escape') this._sluit(); };
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    window.removeEventListener('keydown', this._onKey);
    super.disconnectedCallback();
  }

  get _actief() {
    const tab = this._tab || this.start || 'intro';
    return tab === 'intro' || NEN5740_UITLEG.some((s) => s.code === tab) ? tab : 'intro';
  }

  _sluit() {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }

  _kies(tab) {
    this._tab = tab;
    // Elk tabblad begint bovenaan; anders open je VEP-OO halverwege de tabel van ONV-NL.
    this.renderRoot.querySelector('.inhoud')?.scrollTo(0, 0);
  }

  render() {
    const actief = this._actief;
    return html`
      <div class="achtergrond" @click=${(e) => e.target === e.currentTarget && this._sluit()}>
        <div class="venster" role="dialog" aria-label="Uitleg onderzoeksstrategieën NEN 5740">
          <header>
            <div>
              <h2>Onderzoeksstrategieën</h2>
              <span>Volgens ${NEN5740_BRON}</span>
            </div>
            <button class="sluit" @click=${this._sluit} title="Sluiten">${unsafeSVG(lucideIcon(X, 18))}</button>
          </header>
          <div class="lijf">
            <nav>
              <button class=${actief === 'intro' ? 'aan' : ''} @click=${() => this._kies('intro')}>
                <strong>Introductie</strong><small>Welke strategie kies je</small>
              </button>
              <span class="groep">Strategieën</span>
              ${NEN5740_UITLEG.map((s) => html`
                <button class=${actief === s.code ? 'aan' : ''} @click=${() => this._kies(s.code)}>
                  <strong>${s.code}</strong><small>${s.naam}</small>
                </button>`)}
            </nav>
            <div class="inhoud">
              ${actief === 'intro' ? this._renderIntro() : this._renderStrategie(NEN5740_UITLEG.find((s) => s.code === actief))}
            </div>
          </div>
        </div>
      </div>`;
  }

  _renderIntro() {
    const i = NEN5740_INTRO;
    return html`
      <h3>${i.titel}</h3>
      ${i.alineas.map((a) => html`<p>${a}</p>`)}
      ${this._renderTabel(i.overzicht)}
      <p class="bron">Bron: ${NEN5740_BRON}, ${i.overzicht.bron}.</p>`;
  }

  _renderStrategie(s) {
    return html`
      <div class="titel">
        <h3>${s.code}</h3>
        <span>${s.naam}</span>
      </div>
      <section>
        <h4>Wanneer van toepassing</h4>
        <p>${s.uitleg}</p>
      </section>
      ${s.voorbeelden?.length ? html`
        <section>
          <h4>Voorbeelden</h4>
          <ul class="voorbeelden">${s.voorbeelden.map((v) => html`<li>${v}</li>`)}</ul>
        </section>` : ''}
      <section>
        <h4>Aantallen volgens de norm</h4>
        ${s.tabellen.map((t) => this._renderTabel(t))}
      </section>
      <p class="bron">Bron: ${NEN5740_BRON}, ${s.bron}. De voetnoten zijn samengevat; bij twijfel geldt de tekst van de norm.</p>`;
  }

  _renderTabel(t) {
    return html`
      <figure>
        <figcaption>${t.titel}</figcaption>
        <div class="rol">
          <table>
            <thead>
              ${t.kop.map((rij) => html`
                <tr>${rij.map((cel) => html`<th colspan=${cel.cs || 1} rowspan=${cel.rs || 1}>${cel.t}</th>`)}</tr>`)}
            </thead>
            <tbody>
              ${t.rijen.map((rij) => html`<tr>${rij.map((cel, i) => html`<td class=${i === 0 ? 'eerste' : ''}>${cel}</td>`)}</tr>`)}
            </tbody>
          </table>
        </div>
        ${t.noten?.length ? html`
          <ul class="noten">
            ${t.noten.map(([letter, tekst]) => html`<li>${letter ? html`<b>${letter}</b>` : ''}<span>${tekst}</span></li>`)}
          </ul>` : ''}
      </figure>`;
  }

  static styles = css`${unsafeCSS(base)}
    :host { display:contents; }
    .achtergrond { position:fixed; inset:0; z-index:var(--mv-z-modal, 1000); background:rgba(16,28,18,.5);
                   display:flex; align-items:center; justify-content:center; padding:24px; }
    .venster { width:100%; max-width:1100px; height:min(86vh, 860px); display:flex; flex-direction:column;
               background:var(--mv-surface); border-radius:16px; overflow:hidden; box-shadow:0 24px 70px rgba(0,0,0,.3); }
    header { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; padding:18px 22px 14px;
             border-bottom:1px solid var(--mv-border); }
    header h2 { margin:0; font-size:1.1rem; font-weight:700; color:var(--mv-text); }
    header span { font-size:.8rem; color:var(--mv-text-muted); }
    .sluit { display:flex; padding:6px; border:none; border-radius:8px; background:none; color:var(--mv-text-sub); cursor:pointer; }
    .sluit:hover { background:var(--mv-surface-alt); }
    .lijf { flex:1; min-height:0; display:grid; grid-template-columns:250px minmax(0,1fr); }
    nav { display:flex; flex-direction:column; gap:2px; padding:14px 12px; overflow-y:auto;
          background:var(--mv-surface-alt); border-right:1px solid var(--mv-border); }
    nav .groep { margin:12px 10px 4px; font-size:.68rem; font-weight:800; letter-spacing:.06em; text-transform:uppercase;
                 color:var(--mv-text-muted); }
    nav button { display:flex; flex-direction:column; gap:1px; padding:8px 10px; text-align:left; font-family:inherit;
                 border:1px solid transparent; border-radius:9px; background:none; cursor:pointer; }
    nav button:hover { background:var(--mv-surface); }
    nav button.aan { background:var(--mv-surface); border-color:var(--mv-primary); }
    nav strong { font-size:.85rem; font-weight:700; color:var(--mv-text); }
    nav small { font-size:.74rem; color:var(--mv-text-sub); }
    nav button.aan strong { color:var(--mv-primary-hover); }
    .inhoud { overflow-y:auto; padding:22px 28px 28px; }
    .titel { display:flex; align-items:baseline; gap:10px; flex-wrap:wrap; margin-bottom:6px; }
    h3 { margin:0 0 6px; font-size:1.15rem; font-weight:700; color:var(--mv-text); }
    .titel h3 { margin:0; }
    .titel span { font-size:.88rem; color:var(--mv-text-sub); }
    p { margin:0 0 12px; max-width:78ch; font-size:.88rem; line-height:1.6; color:var(--mv-text); }
    p.bron { margin-top:10px; font-size:.76rem; color:var(--mv-text-muted); }
    section { margin-top:18px; }
    section:first-of-type { margin-top:10px; }
    h4 { margin:0 0 6px; font-size:.72rem; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:var(--mv-text-muted); }
    .voorbeelden { margin:0; padding-left:18px; display:flex; flex-direction:column; gap:3px; max-width:78ch; }
    .voorbeelden li { font-size:.88rem; line-height:1.5; color:var(--mv-text); }
    figure { margin:10px 0 0; }
    figure + figure { margin-top:18px; }
    figcaption { margin-bottom:8px; font-size:.84rem; font-weight:700; color:var(--mv-text); }
    .rol { overflow-x:auto; border:1px solid var(--mv-border); border-radius:10px; }
    table { width:100%; border-collapse:collapse; font-size:.8rem; }
    th, td { padding:7px 10px; border-bottom:1px solid var(--mv-border); border-right:1px solid var(--mv-border); text-align:center; }
    th:last-child, td:last-child { border-right:none; }
    tbody tr:last-child td { border-bottom:none; }
    th { background:var(--mv-surface-alt); font-weight:700; color:var(--mv-text-sub); vertical-align:bottom; line-height:1.3; }
    td { font-variant-numeric:tabular-nums; color:var(--mv-text); }
    td.eerste { text-align:left; font-weight:600; white-space:nowrap; }
    tbody tr:hover td { background:var(--mv-surface-alt); }
    .noten { list-style:none; margin:8px 0 0; padding:0; display:flex; flex-direction:column; gap:4px; }
    .noten li { display:flex; gap:8px; font-size:.76rem; line-height:1.45; color:var(--mv-text-sub); }
    .noten b { flex:none; width:12px; font-weight:700; color:var(--mv-text); }
    @media (max-width: 760px) {
      .lijf { grid-template-columns:1fr; grid-template-rows:auto minmax(0,1fr); }
      nav { flex-direction:row; overflow-x:auto; border-right:none; border-bottom:1px solid var(--mv-border); }
      nav .groep { display:none; }
      nav button { flex:none; }
    }
  `;
}

customElements.define('nen5740-uitleg-dialog', Nen5740UitlegDialog);
